// TMOS Activity Dashboard Page
// Date: 2025-11-17
// Purpose: Platform Admin page for viewing user activity analytics

import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { 
  activityService, 
  ActivityLog, 
  LoginStats,
  ActiveUserStats,
  ErrorRateStats,
  HotPage,
  TenantStats
} from '@/services/activityService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '@/services/api';

type TimeRange = 'today' | '7d' | '30d' | 'thisMonth' | 'lastMonth' | 'custom';

export const ActivityDashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loginStats, setLoginStats] = useState<LoginStats | null>(null);
  const [activeUsers, setActiveUsers] = useState<ActiveUserStats | null>(null);
  const [errorRate, setErrorRate] = useState<ErrorRateStats | null>(null);
  const [hotPages, setHotPages] = useState<HotPage[]>([]);
  const [tenantStats, setTenantStats] = useState<TenantStats[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [allUsers, setAllUsers] = useState<Array<{id: string; first_name: string; last_name: string; email: string}>>([]);
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [timeRange, customStartDate, customEndDate, excludedUserIds]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users/admin/users?limit=1000');
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        setAllUsers(users.map((u: any) => ({
          id: u.id,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || ''
        })));
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
    }
  };

  const getDateRange = (): { start_date?: string; end_date?: string } => {
    const now = new Date();
    let start: Date | undefined;
    let end: Date | undefined;

    switch (timeRange) {
      case 'today':
        // Today means from today to tomorrow (inclusive of today, exclusive of tomorrow)
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case '7d':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        end = now;
        break;
      case '30d':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        end = now;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        if (customStartDate) start = new Date(customStartDate);
        if (customEndDate) end = new Date(customEndDate);
        break;
    }

    return {
      start_date: start?.toISOString().split('T')[0],
      end_date: end?.toISOString().split('T')[0]
    };
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange();

      // Load all stats in parallel
      const [stats, pages, tenants, logs] = await Promise.all([
        activityService.getStats(dateRange.start_date, dateRange.end_date),
        activityService.getHotPages(dateRange.start_date, dateRange.end_date, 'visits', 10),
        activityService.getTenantStats(dateRange.start_date, dateRange.end_date),
        activityService.getActivityLogs({ 
          ...dateRange,
          exclude_user_ids: excludedUserIds.length > 0 ? excludedUserIds : undefined,
          limit: 100
        })
      ]);

      setLoginStats(stats.logins);
      setActiveUsers(stats.activeUsers);
      setErrorRate(stats.errorRate);
      setHotPages(pages);
      setTenantStats(tenants);
      setActivityLogs(logs);
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      toast.error(error.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Format IP address to numeric format (handle IPv6, IPv4, etc.)
  const formatIPAddress = (ip: string): string => {
    if (!ip) return '-';
    
    // If it's already in IPv4 format (xxx.xxx.xxx.xxx), return as is
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) {
      return ip;
    }
    
    // If it's IPv6, try to extract IPv4 from IPv6-mapped IPv4 (::ffff:xxx.xxx.xxx.xxx)
    if (ip.startsWith('::ffff:')) {
      const ipv4 = ip.replace('::ffff:', '');
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ipv4)) {
        return ipv4;
      }
    }
    
    // If it's a PostgreSQL inet type that includes subnet, extract just the IP
    if (ip.includes('/')) {
      const ipPart = ip.split('/')[0];
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ipPart)) {
        return ipPart;
      }
    }
    
    // For other formats, try to extract numeric parts
    // If all else fails, return the original (might be IPv6 or other format)
    return ip;
  };

  const handleExport = async () => {
    try {
      const dateRange = getDateRange();
      const blob = await activityService.exportToCSV({ ...dateRange });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Activity data exported successfully');
    } catch (error: any) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary-bg)] mx-auto"></div>
          <p className="mt-4 text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          {/* Header */}
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="w-5 h-5 text-[var(--color-primary-bg)]" />
                <div>
                  <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                    {t('admin.activity.title') || 'Activity Dashboard'}
                  </h1>
                  <p className="text-xs text-[var(--color-secondary-text)]">
                    {t('admin.activity.subtitle') || 'User activity analytics and monitoring'}
                  </p>
                </div>
              </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FunnelIcon className="w-5 h-5 mr-2" />
                {t('common.filters') || 'Filters'}
                {excludedUserIds.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                    {excludedUserIds.length}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                {t('common.export') || 'Export'}
              </Button>
            </div>
          </div>
        </div>

          {/* Time Range Selector */}
          <Card variant="outline" padding="md" className="mb-2">
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="text-sm font-medium text-[var(--color-secondary-text)]">
                {t('admin.activity.timeRange') || 'Time Range:'}
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                className="px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
              >
                <option value="today">{t('admin.activity.today') || 'Today'}</option>
                <option value="7d">{t('admin.activity.last7Days') || 'Last 7 Days'}</option>
                <option value="30d">{t('admin.activity.last30Days') || 'Last 30 Days'}</option>
                <option value="thisMonth">{t('admin.activity.thisMonth') || 'This Month'}</option>
                <option value="lastMonth">{t('admin.activity.lastMonth') || 'Last Month'}</option>
                <option value="custom">{t('admin.activity.custom') || 'Custom'}</option>
              </select>
              {timeRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                  />
                  <span className="text-[var(--color-secondary-text)]">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                  />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <Card variant="outline" padding="md">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {t('admin.activity.totalLogins') || 'Total Logins'}
                  </p>
                  <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                    {loginStats?.total.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    Today: {loginStats?.today || 0}
                  </p>
                </div>
                <UserGroupIcon className="w-10 h-10 text-[var(--color-primary-bg)]" />
              </div>
            </CardContent>
          </Card>

          <Card variant="outline" padding="md">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {t('admin.activity.activeUsers') || 'Active Users'}
                  </p>
                  <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                    {activeUsers?.total.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    This week: {activeUsers?.thisWeek || 0}
                  </p>
                </div>
                <UserGroupIcon className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card variant="outline" padding="md">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {t('admin.activity.errorRate') || 'Error Rate'}
                  </p>
                  <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                    {errorRate?.rate.toFixed(2) || 0}%
                  </p>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    Total errors: {errorRate?.total || 0}
                  </p>
                </div>
                <ExclamationTriangleIcon className="w-10 h-10 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card variant="outline" padding="md">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[var(--color-secondary-text)]">
                    {t('admin.activity.tenants') || 'Tenants'}
                  </p>
                  <p className="text-2xl font-bold text-[var(--color-surface-text)]">
                    {tenantStats.length}
                  </p>
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                    {t('admin.activity.active') || 'Active'}
                  </p>
                </div>
                <ChartBarIcon className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Login Trends */}
          <Card variant="outline" padding="md">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)]">
                {t('admin.activity.loginTrends') || 'Login Trends'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={loginStats?.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#0088FE" name="Logins" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Active Users Trends */}
          <Card variant="outline" padding="md">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)]">
                {t('admin.activity.activeUsersTrend') || 'Active Users Trend'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activeUsers?.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#00C49F" name="Active Users" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Hot Pages and Tenant Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Hot Pages */}
          <Card variant="outline" padding="md">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)]">
                {t('admin.activity.hotPages') || 'Most Visited Pages'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hotPages.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="page_name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visits" fill="#8884d8" name="Visits" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tenant Distribution */}
          <Card variant="outline" padding="md">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)]">
                {t('admin.activity.tenantDistribution') || 'Activity by Tenant'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tenantStats.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ tenant_name, total_activities }) => `${tenant_name}: ${total_activities}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="total_activities"
                  >
                    {tenantStats.slice(0, 6).map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card variant="outline" padding="md" className="mb-6">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)]">
                {t('common.filters') || 'Filters'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* User Exclusion Filter */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    {t('admin.activity.excludeUsers') || 'Exclude Users'}
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {excludedUserIds.map((userId) => {
                      const user = allUsers.find(u => u.id === userId);
                      return (
                        <span
                          key={userId}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm"
                        >
                          {user ? `${user.first_name} ${user.last_name}`.trim() || user.email : userId.substring(0, 8)}
                          <button
                            onClick={() => {
                              setExcludedUserIds(excludedUserIds.filter(id => id !== userId));
                            }}
                            className="hover:bg-red-200 rounded-full p-0.5"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      const userId = e.target.value;
                      if (userId && !excludedUserIds.includes(userId)) {
                        setExcludedUserIds([...excludedUserIds, userId]);
                      }
                      e.target.value = '';
                    }}
                    className="w-full px-3 py-2 rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)]"
                  >
                    <option value="">{t('admin.activity.selectUserToExclude') || 'Select user to exclude...'}</option>
                    {allUsers
                      .filter(u => !excludedUserIds.includes(u.id))
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {`${user.first_name} ${user.last_name}`.trim() || user.email} ({user.email})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Logs Table */}
        <Card variant="outline" padding="md">
          <CardHeader>
            <CardTitle className="text-[var(--color-surface-text)]">
              {t('admin.activity.recentActivity') || 'Recent Activity'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.time') || 'Time'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.user') || 'User'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.type') || 'Type'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.page') || 'Page'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.ip') || 'IP Address'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--color-surface-text)]">
                      {t('admin.activity.error') || 'Error'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-[var(--color-secondary-text)]">
                        {t('admin.activity.noActivity') || 'No activity found'}
                      </td>
                    </tr>
                  ) : (
                    activityLogs.map((log) => (
                      <tr key={log.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-secondary-bg)]">
                        <td className="py-3 px-4 text-sm text-[var(--color-surface-text)]">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-surface-text)]">
                          {log.user_id ? (
                            <div 
                              className="cursor-help underline decoration-dotted"
                              title={log.user_email || 'No email available'}
                            >
                              {log.user_first_name || log.user_last_name 
                                ? `${log.user_first_name || ''} ${log.user_last_name || ''}`.trim()
                                : log.user_email || log.user_id.substring(0, 8) + '...'}
                            </div>
                          ) : (
                            'Anonymous'
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-surface-text)]">
                          <span className={`px-2 py-1 rounded text-xs ${
                            log.activity_type === 'LOGIN' ? 'bg-green-100 text-green-800' :
                            log.activity_type === 'LOGOUT' ? 'bg-gray-100 text-gray-800' :
                            log.activity_type === 'PAGE_VIEW' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {log.activity_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-surface-text)]">
                          {log.page_name || log.page_path || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-surface-text)]">
                          {log.ip_address ? formatIPAddress(log.ip_address) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {log.error_occurred ? (
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

